import {
  BodyText,
  Caption,
  TextField,
  Message,
  Spacer,
  Stack
} from '@kvdbil/components';
import React, {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useState
} from 'react';
import styled from 'styled-components';
import { MAX_BID_AMOUNT } from '~/config/constants';
import { isBelowMaxBid, isValidBidIncrement } from '~/helpers/bid';
import {
  formatMoney,
  formatMoneyWithoutCurrency,
  inclVATtoExclVAT,
  exclVATtoInclVAT
} from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import BiddingButton from './BiddingButton';
import ToolTip from './Tooltip';
import { useFeatureFlag } from '~/App/components/FeatureFlags';
import BidFormMonthlyCost from './BidFormMonthlyCost';

const TooltipContainer = styled.div`
  margin-top: 1rem;
`;

const StyledErrorMessage = styled(Message).attrs({
  type: 'error',
  fullWidth: true
})`
  margin-top: -0.25rem;
  margin-bottom: 0.5rem;
`;

const StyledCaption = styled(BodyText)`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const MaxBidInformation = styled.div`
  font-size: 1em;
  line-height: 1.5em;
  color: ${({ theme }) => theme.colors.text.dark};
  padding-bottom: 1em;
`;

const TextFieldWrapper = styled.div`
  margin-top: 0.25rem;
  margin-bottom: 0.75rem;
`;

export interface BidFormProps {
  error?: string;
  minBidRaise: number;
  lowestBidToPlace: number;
  leadingBidAmount?: number;
  isLoading: boolean;
  isDisabled: boolean;
  isConfirmationOpen: boolean;
  onPlaceBid(bidAmount: number): void;
  showExclVat?: boolean;
  vatExcludedBiddingAllowed: boolean;
}
export const BidForm = ({
  error,
  isLoading,
  isDisabled,
  minBidRaise,
  lowestBidToPlace,
  leadingBidAmount,
  onPlaceBid,
  isConfirmationOpen,
  showExclVat = false,
  vatExcludedBiddingAllowed
}: BidFormProps) => {
  const { t } = useTranslation();
  const { value: isEmptyFieldAllowed } = useFeatureFlag(
    'empty-bid-field-allowed'
  );

  const [inputError, setInputError] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [isConfirmation, setIsconfirmation] = useState(false);

  const displayError = error || inputError;
  const isBidTooHigh = !isBelowMaxBid(bidAmount);
  const isMaxBid = Number(bidAmount) > lowestBidToPlace;
  const isNoBidAmount = bidAmount === '';
  const isBidExcessive =
    leadingBidAmount && Number(bidAmount) >= leadingBidAmount * 10;

  useEffect(() => {
    setBidAmount('');
    setIsconfirmation(false);
  }, [isConfirmationOpen]);

  const handleEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onBidPreventDefault(event);
    }
  };

  const onBidPreventDefault = (
    event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    if (!isEmptyFieldAllowed && 'code' in event && event.code === 'Enter')
      return;

    if (!bidAmount) {
      setBidAmount(lowestBidToPlace.toString());
      setIsconfirmation(true);
      return;
    }

    if (Number(bidAmount) < lowestBidToPlace) {
      setInputError(
        t(
          'This bid is too low, the minimum bid is %1$s. Please try again.',
          formatMoney({
            value: lowestBidToPlace,
            currency: t('SEK')
          })
        )
      );

      return;
    }

    if (!isValidBidIncrement(Number(bidAmount), minBidRaise)) {
      setInputError(
        t(
          'Bids must be in even %1$s kr increments. Please adjust your bid.',
          formatMoneyWithoutCurrency(minBidRaise.toString())
        )
      );

      return;
    }

    setIsconfirmation(true);
  };

  const onConfirmBid = (
    event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    onPlaceBid(Number(bidAmount));
  };

  const handleBidAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    inputError && setInputError('');

    const valueWithoutSpaces = event.target.value.replace(/ /g, '');

    if (valueWithoutSpaces === '' || valueWithoutSpaces === '0') {
      return setBidAmount('');
    }

    Number.isInteger(Number(valueWithoutSpaces)) &&
      setBidAmount(valueWithoutSpaces);
  };
  const formattedLowestBidToPlace = formatMoney({
    value: lowestBidToPlace,
    currency: t('SEK')
  });

  return (
    <div>
      {!isDisabled && (
        <Stack>
          {vatExcludedBiddingAllowed && (
            <Message type="warning" fullWidth>
              {t('Please note that bidding for this item is exclusive of VAT.')}
            </Message>
          )}
          <TooltipContainer>
            <ToolTip
              label={
                vatExcludedBiddingAllowed
                  ? t(
                      'Minimum bid to place: %1$s (excl VAT)',
                      formattedLowestBidToPlace
                    )
                  : t('Minimum bid to place: %1$s', formattedLowestBidToPlace)
              }
              content={
                <Caption>
                  {vatExcludedBiddingAllowed
                    ? t('Lowest bid to place info excl VAT')
                    : t('Lowest bid to place info')}
                </Caption>
              }
              alignContent={'start'}
            />
          </TooltipContainer>
        </Stack>
      )}
      {!isDisabled && isConfirmation && isMaxBid && (
        <MaxBidInformation>
          {t(
            'KVD automatically bids until your max bid is reached if you are overbidded.'
          )}
        </MaxBidInformation>
      )}
      {!isDisabled && !isConfirmation && (
        <TextFieldWrapper>
          <TextField
            suffix={t('SEK')}
            value={isNoBidAmount ? '' : formatMoneyWithoutCurrency(bidAmount)}
            onChange={handleBidAmountChange}
            onKeyDown={handleEnter}
            placeholder={
              vatExcludedBiddingAllowed
                ? t('Place bid (excl VAT)')
                : t('Place bid')
            }
            inputMode="numeric"
            label={
              vatExcludedBiddingAllowed ? t('Place bid (excl VAT)') : undefined
            }
          />
        </TextFieldWrapper>
      )}
      {isBidTooHigh && (
        <StyledErrorMessage>
          {t(
            'The maximum allowed bid is %1$s',
            formatMoney({
              value: MAX_BID_AMOUNT,
              currency: t('SEK')
            })
          )}
        </StyledErrorMessage>
      )}

      {showExclVat && !isBidTooHigh && (
        <StyledCaption>
          {!vatExcludedBiddingAllowed
            ? t(
                'Your planned bid excl VAT: %1$s',
                (bidAmount &&
                  formatMoney({
                    value: inclVATtoExclVAT(parseInt(bidAmount, 10)),
                    currency: t('SEK')
                  })) ||
                  '-'
              )
            : t(
                'Your planned bid incl VAT: %1$s',
                (bidAmount &&
                  formatMoney({
                    value: exclVATtoInclVAT(parseInt(bidAmount, 10)),
                    currency: t('SEK')
                  })) ||
                  '-'
              )}
        </StyledCaption>
      )}

      {displayError && <StyledErrorMessage>{displayError}</StyledErrorMessage>}

      {isConfirmation && isBidExcessive && (
        <>
          <Message type="warning" fullWidth>
            {t(
              'Your bid is more than 10 times higher than the leading bid. Are you sure this is correct?'
            )}
          </Message>
          <Spacer height={2} />
        </>
      )}

      {!isDisabled && !showExclVat && !isConfirmation && (
        <div style={{ marginBottom: '2rem' }}>
          <BidFormMonthlyCost bidAmount={bidAmount} />
        </div>
      )}

      <BiddingButton
        bidAmount={bidAmount}
        isMaxBid={isMaxBid}
        isLoading={isLoading}
        isDisabled={
          isEmptyFieldAllowed ? isDisabled : isDisabled || isNoBidAmount
        }
        isConfirmation={isConfirmation}
        onPlaceBid={onBidPreventDefault}
        onConfirmBid={onConfirmBid}
      />
    </div>
  );
};

export default BidForm;
